import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  DOCUMENT,
  inject,
  Inject,
  OnInit,
  signal,
} from '@angular/core';
import { ClientAuthService } from 'libs/core/src/lib/services/client-auth.service';
import { ClientsService } from 'libs/core/src/lib/services/clients.service';
import { environment } from 'libs/core/src/lib/environments/environment';
import { IcImpersonationBannerComponent } from 'libs/core/src/lib/componets/ic-impersonation-banner.component';
import { IcNoticeBannerComponent } from 'libs/core/src/lib/componets/ic-notice-banner.component';
import { InsurerService } from 'libs/core/src/lib/services/insurer.service';
import { PartnersService } from 'libs/core/src/lib/services/partners.service';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterOutlet,
} from '@angular/router';
import { forkJoin, of, switchMap, tap } from 'rxjs';
import { filter, finalize, map, take } from 'rxjs/operators';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { IErrorModel } from '@inshuwa-client/models';
import { getVersionInfo } from '@inshuwa-client/shared-version';
import { IcUpdateToastComponent } from 'libs/shared-modules/src/lib/update-toast/ic-update-toast.component';
import { NzSpinComponent } from 'ng-zorro-antd/spin';
import { NzIconModule } from 'ng-zorro-antd/icon';

@Component({
  selector: 'ic-client-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ngx-loading-bar color="var(--primary-color, #520B4D)"></ngx-loading-bar>
    @if (isLoading()) {
      <div class="flex items-center justify-center min-h-[100dvh]">
        <div>
          <nz-spin [nzSize]="'large'" [nzSpinning]="true" />
          <p class="text-muted-foreground text-center mt-6">
            Loading... Please wait
          </p>
        </div>
      </div>
    } @else {
      <div class="h-[100dvh] flex flex-col">
        <ic-impersonation-banner />
        <ic-notice-banner />
        <main class="flex-1 min-h-0 overflow-y-auto">
          <router-outlet></router-outlet>
        </main>
        <ic-update-toast />
<!--UNCOMMENT TO RESTORE FOOTER -->
<!--        <footer-->
<!--          class="flex flex-col gap-1 sm:gap-2 sm:flex-row py-3 sm:py-6 w-full shrink-0 items-center px-4 md:px-6 border-t bg-background"-->
<!--        >-->
<!--          <p class="text-[11px] sm:text-xs text-muted-foreground">-->
<!--            {{ year }} Inshuwa. All rights reserved.-->
<!--          </p>-->
<!--          <span class="text-[10px] text-gray-400 ml-2"-->
<!--            >v{{ versionInfo.version }}</span-->
<!--          >-->
<!--        </footer>-->
      </div>
    }
  `,
  styles: ``,
  imports: [
    RouterOutlet,
    LoadingBarModule,
    NzSpinComponent,
    NzIconModule,
    IcImpersonationBannerComponent,
    IcNoticeBannerComponent,
    IcUpdateToastComponent,
  ],
  providers: [],
})
export class AppComponent implements OnInit {
  env = environment;
  versionInfo = getVersionInfo();
  insureService = inject(InsurerService);
  route = inject(ActivatedRoute);
  router = inject(Router);
  partnerService = inject(PartnersService);
  authService = inject(ClientAuthService);
  notification = inject(NzNotificationService);
  clientService = inject(ClientsService);
  destroyRef = inject(DestroyRef);
  year = new Date().getFullYear();
  protected readonly isLoading = signal(false);

  constructor(@Inject(DOCUMENT) private document: Document) {
    this.document
      .getElementById('appFavicon')
      ?.setAttribute('href', environment.favIcon);
  }

  ngOnInit(): void {
    const insurer$ = environment.insurerId
      ? this.insureService.getInsurerById(environment.insurerId)
      : of(null);

    const partner$ = environment.partnerId
      ? this.partnerService.getById(environment.partnerId)
      : of(null);

    const data$ = forkJoin([insurer$, partner$]);

    this.isLoading.set(true);
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        take(1),
        map(() => {
          const qp = this.route.snapshot.queryParamMap;
          return {
            ssoToken: qp.get('x-sso-token'),
            msisdn: qp.get('msisdn'),
            firstName: qp.get('firstName') ?? undefined,
            lastName: qp.get('lastName') ?? undefined,
            apiKey: qp.get('apiKey') ?? undefined,
          };
        }),
        tap(({ ssoToken, msisdn, apiKey }) => {
          if (ssoToken || msisdn || apiKey) {
            this.router.navigate([], {
              queryParams: {
                'x-sso-token': null,
                msisdn: null,
                firstName: null,
                lastName: null,
                apiKey: null,
              },
              queryParamsHandling: 'merge',
              replaceUrl: true,
            });
          }
        }),
        switchMap(({ ssoToken, msisdn, firstName, lastName, apiKey }) => {
          if (ssoToken) {
            localStorage.setItem('x-sso-token', ssoToken);
            return this.authService
              .SSOLogin({ token: ssoToken, apiKey, partnerId: this.env.partnerId! })
              .pipe(
                switchMap(() => this.clientService.clientGuard()),
                switchMap(() => data$),
                map((result) => ({ result, ssoToken, msisdn: null })),
              );
          }

          if (msisdn) {
            return this.authService
              .SSOLogin({ msisdn, firstName, lastName, apiKey, partnerId: this.env.partnerId! })
              .pipe(
                switchMap(() => this.clientService.clientGuard()),
                switchMap(() => data$),
                map((result) => ({ result, ssoToken: null, msisdn })),
              );
          }

          return data$.pipe(map((result) => ({ result, ssoToken: null, msisdn: null })));
        }),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.isLoading.set(false)),
      )
      .subscribe({
        next: ({ result: [insurer, partner] }) => {
          if (insurer) this.insureService.saveToLocalStorage(insurer);
          if (partner) this.partnerService.saveToLocalStorage(partner);
        },
        error: (err: IErrorModel) => {
          const qp = this.route.snapshot.queryParamMap;
          const hasSsoParams =
            qp.has('x-sso-token') || qp.has('msisdn');
          if (hasSsoParams) {
            this.notification.error(
              'Login Failure',
              err.message || 'An error occurred during login. Contact support.',
            );
          }
        },
      });
  }
}
